export type AnalyticsPrimitive = string | number | boolean | null

export type AnalyticsPayload = Record<string, AnalyticsPrimitive | AnalyticsPrimitive[] | undefined>

export type AnalyticsEvent = AnalyticsPayload & {
  event: string
}

type AnalyticsContext = {
  siteKey?: string
  siteLocale?: string
}

export type PurposeFormSubmitEventName =
  | 'dealer_request_submit'
  | 'product_enquiry_submit'
  | 'contact_form_submit'
  | 'warranty_registration_submit'
  | 'job_application_submit'
  | 'form_submit'

export type CtaClickEventName = 'cta_click'

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>
    __analyticsContext?: AnalyticsContext
  }
}

const isBrowser = () => typeof window !== 'undefined'
const analyticsDebugCookieKey = 'analytics_debug'

const isAnalyticsDebugCookieEnabled = () =>
  isBrowser() &&
  document.cookie.split(';').some((entry) => entry.trim() === `${analyticsDebugCookieKey}=true`)

const isAnalyticsDebugEnabled = () =>
  process.env.NEXT_PUBLIC_ANALYTICS_DEBUG === 'true' || isAnalyticsDebugCookieEnabled()

export const isAnalyticsDebugActive = () => isAnalyticsDebugEnabled()

export const applyAnalyticsDebugSettingFromQuery = (value?: string | null): void => {
  if (!isBrowser() || !value) return

  const normalized = value.trim().toLowerCase()
  const enabledValues = new Set(['1', 'true', 'on', 'yes'])
  const disabledValues = new Set(['0', 'false', 'off', 'no'])

  if (enabledValues.has(normalized)) {
    document.cookie = `${analyticsDebugCookieKey}=true; path=/; max-age=31536000; samesite=lax`
    return
  }

  if (disabledValues.has(normalized)) {
    document.cookie = `${analyticsDebugCookieKey}=; path=/; max-age=0; samesite=lax`
  }
}

export const pushDataLayerEvent = (event: AnalyticsEvent): void => {
  if (!isBrowser()) return

  const context = window.__analyticsContext
  const resolvedEvent: AnalyticsEvent = {
    ...context,
    ...event,
    siteKey: event.siteKey ?? context?.siteKey,
    siteLocale: event.siteLocale ?? context?.siteLocale,
  }

  window.dataLayer = window.dataLayer || []
  window.dataLayer.push(resolvedEvent)

  if (isAnalyticsDebugEnabled()) {
    console.info('[analytics]', resolvedEvent)
  }
}

export const setAnalyticsContext = (context: AnalyticsContext): void => {
  if (!isBrowser()) return
  window.__analyticsContext = {
    ...window.__analyticsContext,
    ...context,
  }
}

export const buildPageViewEvent = ({
  siteKey,
  siteLocale,
  pagePath,
  pageTitle,
  pageUrl,
}: {
  siteKey: string
  siteLocale?: string
  pagePath: string
  pageTitle?: string
  pageUrl?: string
}): AnalyticsEvent => ({
  event: 'page_view',
  siteKey,
  siteLocale,
  pagePath,
  pageTitle,
  pageUrl,
})

export const buildFilterUseEvent = ({
  filterType,
  filterValue,
}: {
  filterType: string
  filterValue: string
}): AnalyticsEvent => ({
  event: 'filter_use',
  filter_type: filterType,
  filter_value: filterValue,
})

export const buildCtaClickEvent = ({
  ctaName,
  location,
  ctaText,
  ctaHref,
  ctaVariant,
}: {
  ctaName: string
  location?: string
  ctaText?: string
  ctaHref?: string
  ctaVariant?: string
}): AnalyticsEvent => ({
  event: 'cta_click' satisfies CtaClickEventName,
  cta_name: ctaName,
  location,
  cta_text: ctaText,
  cta_href: ctaHref,
  cta_variant: ctaVariant,
})

export const buildFormSubmitEvent = ({
  eventName,
  formName,
  formLocale,
  formValid,
  errorFields,
  fieldCount,
  requiredFieldCount,
}: {
  eventName: PurposeFormSubmitEventName
  formName: string
  formLocale?: string
  formValid: boolean
  errorFields?: string[]
  fieldCount: number
  requiredFieldCount: number
}): AnalyticsEvent => ({
  event: eventName,
  form_name: formName,
  form_locale: formLocale,
  form_valid: formValid,
  field_count: fieldCount,
  required_field_count: requiredFieldCount,
  error_fields: errorFields,
})

export const resolveFormSubmitEventName = (formName: string): PurposeFormSubmitEventName => {
  if (formName === 'contact-us') return 'contact_form_submit'
  if (formName === 'warranty-registration') return 'warranty_registration_submit'
  if (formName === 'lead-generation') return 'dealer_request_submit'
  if (formName === 'job-application') return 'job_application_submit'
  return 'form_submit'
}
